import { matchIsValidTel } from "mui-tel-input";

/**
 * Thrown when a phone number fails E.164 formatting/validation.
 */
export class InvalidPhoneFormatError extends Error {
  constructor(
    message = "Invalid phone number format",
    public readonly originalValue?: string
  ) {
    super(message);
    this.name = "InvalidPhoneFormatError";
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, InvalidPhoneFormatError);
    }
  }
}

/**
 * Masks a phone number for display, hiding all but the last 4 digits.
 * Expects E.164 format (e.g., "+1234567890" -> "+******7890").
 */
export const maskPhone = (phone: string): string => {
  if (phone.length <= 4) {
    return phone;
  }
  const hasPlus = phone.startsWith("+");
  const digits = hasPlus ? phone.slice(1) : phone;
  const last4 = digits.slice(-4);
  const maskedLength = digits.length - 4;
  return (hasPlus ? "+" : "") + "*".repeat(maskedLength) + last4;
};

/**
 * Formats a phone number to E.164 format (+1XXXXXXXXXX) with validation.
 * Uses mui-tel-input validation (libphonenumber-js) to ensure valid phone numbers.
 * Returns undefined if the number is invalid.
 */
export function formatPhoneNumber(number: string): string | undefined {
  if (!number) {
    return undefined;
  }

  const digits = number.replace(/[\D]/g, "");
  if (digits.length < 10 || digits.length > 11) {
    return undefined;
  }

  // Format to E.164
  let formatted: string;
  if (/^\+1\d{10}/.test(number)) {
    formatted = number;
  } else if (digits.startsWith("1") && digits.length === 11) {
    formatted = "+".concat(digits);
  } else {
    formatted = "+1".concat(digits);
  }

  // Validate using mui-tel-input (same library as backend)
  if (!matchIsValidTel(formatted)) {
    return undefined;
  }

  return formatted;
}
